import { HStack, Tooltip } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { dimUntilHoverControl } from '@/modules/content/loop/model/loopChrome';

type Props = {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  onClick: () => void;
};

// A single circular action in the loop's rail. Rests dimmed with the rest of the chrome
// and lifts on its own hover, so the highlight tracks exactly what is being pointed at.
// When it turns active (a like / save), the icon gives a small pop for tactile feedback.
const RailButton = ({ icon: Icon, label, active, onClick }: Props) => {
  const reduceMotion = useReducedMotion();

  return (
    <Tooltip label={label} placement="right" hasArrow>
      <HStack
        as="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={active}
        justify="center"
        width="42px"
        height="42px"
        borderRadius="full"
        backgroundColor="white"
        color={active ? 'primary.500' : 'text.darkBlue'}
        boxShadow="0px 4px 14px rgba(14, 0, 98, 0.10)"
        {...dimUntilHoverControl}
        transition="all 0.15s ease"
        _hover={{ opacity: 1, transform: 'translateY(-2px)', color: 'primary.500' }}
        _focusVisible={{ opacity: 1 }}
        _active={{ transform: 'scale(0.94)' }}
      >
        <motion.span
          style={{ display: 'inline-flex' }}
          animate={reduceMotion ? undefined : { scale: active ? [1, 1.35, 1] : 1 }}
          transition={{ duration: 0.32, ease: 'easeOut' }}
        >
          <Icon size={19} strokeWidth={2} fill={active ? 'currentColor' : 'none'} />
        </motion.span>
      </HStack>
    </Tooltip>
  );
};

export default RailButton;
